import React, { useContext, useState } from 'react'
import { Button, Container, Input, InputGroup, Table } from 'reactstrap'

import './ResponsibleUsersPage.css'

import { API } from 'API'
import { AppRoutes } from 'constants/appRoutes'
import { RequestToasterContext } from 'containers/Providers'
import { AccessRole, ResponsibleUser, hasAccessRole } from 'domains/user'
import { useEffectOnce } from 'hooks/useEffectOnce'
import { StandardErrorResponse } from 'types/APIResponses'
import { Nullable } from 'types/utility'
import { resolveTargetRegionKey } from 'utils/resolveTargetRegionKey'

const buildUserHomeRegionUrl = (user: ResponsibleUser): string => {
  const targetRegion = resolveTargetRegionKey(user.region)
  const params = new URLSearchParams({ crossRegion: 'true' })

  if (targetRegion != null) {
    params.set('region', targetRegion)
  }

  return `${AppRoutes.users}/${encodeURIComponent(user.username)}?${params.toString()}`
}

const UsernameLink = ({ user }: { user: ResponsibleUser }) => (
  <a href={buildUserHomeRegionUrl(user)} target="_blank" rel="noopener noreferrer">
    {user.username}
  </a>
)

export const ResponsibleUsersPage = () => {
  const { requestStatusRef } = useContext(RequestToasterContext)
  const [users, setUsers] = useState<ResponsibleUser[]>([])
  const [expandedUser, setExpandedUser] = useState<Nullable<string>>(null)
  const [subordinates, setSubordinates] = useState<Record<string, ResponsibleUser[]>>({})
  const [loadingUser, setLoadingUser] = useState<Nullable<string>>(null)
  const [searchInput, setSearchInput] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [isSearchLoading, setIsSearchLoading] = useState(false)

  const canExpandUser = (user: ResponsibleUser) => hasAccessRole(user.userRole, AccessRole.ADMIN)

  useEffectOnce(() => {
    API.getResponsibleUsers()
      .then((response) => {
        setUsers(response)
      })
      .catch((error: StandardErrorResponse) => {
        setUsers([])
        requestStatusRef.current?.showAlert(error.response.data.error, 'danger')
      })
  })

  const toggleExpand = (username: string) => {
    if (expandedUser === username) {
      setExpandedUser(null)
      return
    }

    setExpandedUser(username)

    if (subordinates[username] != null) {
      return
    }

    setLoadingUser(username)
    API.getSubordinateUsers(username)
      .then((response) => {
        setSubordinates((prev) => ({ ...prev, [username]: response }))
      })
      .catch((error: StandardErrorResponse) => {
        requestStatusRef.current?.showAlert(error.response.data.error, 'danger')
      })
      .finally(() => setLoadingUser((current) => (current === username ? null : current)))
  }

  const expandColumnStyle = { width: '1%', minWidth: '40px' }

  const ensureAllSubordinatesLoaded = async () => {
    const missing = users.filter(
      (user) => canExpandUser(user) && subordinates[user.username] == null,
    )

    if (missing.length === 0) {
      return
    }

    setIsSearchLoading(true)
    try {
      const results = await Promise.all(
        missing.map((user) =>
          API.getSubordinateUsers(user.username)
            .then((response) => [user.username, response] as const)
            .catch(() => [user.username, [] as ResponsibleUser[]] as const),
        ),
      )

      setSubordinates((prev) => {
        const next = { ...prev }

        results.forEach(([username, subs]) => {
          next[username] ??= subs
        })
        return next
      })
    } finally {
      setIsSearchLoading(false)
    }
  }

  const applySearch = async () => {
    const term = searchInput.trim()

    if (term) {
      await ensureAllSubordinatesLoaded()
    }

    setSearchTerm(term)
  }

  const onSearchInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      applySearch()
    }
  }

  const normalizedSearchTerm = searchTerm.toLowerCase()
  const matchesSearch = (user: ResponsibleUser) =>
    user.username.toLowerCase().includes(normalizedSearchTerm)
  const getMatchingSubordinates = (user: ResponsibleUser) =>
    (subordinates[user.username] ?? []).filter(matchesSearch)

  const filteredUsers = searchTerm
    ? users.filter((user) => matchesSearch(user) || getMatchingSubordinates(user).length > 0)
    : users

  return (
    <Container>
      <div className="responsible-users-header">
        <h2>My Responsibility Scope</h2>

        <InputGroup className="responsible-users-search">
          <Input
            type="text"
            placeholder="Search by User Name"
            aria-label="Search by User Name"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            onKeyDown={onSearchInputKeyDown}
          />
          <Button color="secondary" onClick={applySearch} disabled={isSearchLoading}>
            {isSearchLoading ? 'Searching...' : 'Search'}
          </Button>
        </InputGroup>
      </div>

      <Table bordered className="responsible-users-table">
        <thead>
          <tr>
            <th style={expandColumnStyle} aria-label="Expand" />
            <th>User Name</th>
            <th>User Role</th>
            <th>Departments</th>
            <th>Countries</th>
          </tr>
        </thead>
        <tbody>
          {filteredUsers.length === 0 ? (
            <tr>
              <td colSpan={5} className="text-center">
                No Users found.
              </td>
            </tr>
          ) : (
            filteredUsers.map((user) => {
              const userSubordinates = subordinates[user.username] ?? []
              const isLoading = loadingUser === user.username
              const canExpand = canExpandUser(user)
              const matchingSubordinates = searchTerm ? getMatchingSubordinates(user) : []
              const isExpanded =
                expandedUser === user.username ||
                (searchTerm !== '' && matchingSubordinates.length > 0)
              const displayedSubordinates =
                searchTerm && matchingSubordinates.length > 0
                  ? matchingSubordinates
                  : userSubordinates

              let subordinatesContent: React.ReactNode

              if (isLoading) {
                subordinatesContent = (
                  <tr>
                    <td colSpan={5} className="text-center">
                      Loading...
                    </td>
                  </tr>
                )
              } else if (displayedSubordinates.length === 0) {
                subordinatesContent = (
                  <tr>
                    <td colSpan={5} className="text-center">
                      No Users found.
                    </td>
                  </tr>
                )
              } else {
                subordinatesContent = (
                  <>
                    {displayedSubordinates.map((subordinate) => (
                      <tr key={subordinate.username}>
                        <td style={expandColumnStyle} />
                        <td style={{ paddingLeft: '2.5rem' }}>
                          <UsernameLink user={subordinate} />
                        </td>
                        <td>{subordinate.userRole}</td>
                        <td>{subordinate.departments.join(', ')}</td>
                        <td>{subordinate.countries.join(', ')}</td>
                      </tr>
                    ))}
                  </>
                )
              }

              return (
                <React.Fragment key={user.username}>
                  <tr className="row-level-1">
                    <td className="text-center">
                      {canExpand && (
                        <Button
                          color="link"
                          className="p-0 responsible-users-expand-toggle"
                          onClick={() => toggleExpand(user.username)}
                          aria-expanded={isExpanded}
                          aria-label={isExpanded ? 'Collapse' : 'Expand'}>
                          {isExpanded ? '▾' : '▸'}
                        </Button>
                      )}
                    </td>
                    <td>
                      <UsernameLink user={user} />
                    </td>
                    <td>{user.userRole}</td>
                    <td>{user.departments.join(', ')}</td>
                    <td>{user.countries.join(', ')}</td>
                  </tr>
                  {canExpand && isExpanded && subordinatesContent}
                </React.Fragment>
              )
            })
          )}
        </tbody>
      </Table>
    </Container>
  )
}
